import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  Avatar,
} from '@mui/material';
import {
  Chat,
  Refresh,
  Visibility,
  OpenInNew,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { atendimentoService, AtendimentoTicket } from '../../services/atendimentoService';
import { formatarPhone } from '../../utils/formatToView';
import VisualizarTicketDialog from '../../pages/atendimento/componentes/VisualizarTicketDialog';
import { getCountryByNumber } from '../../utils/countries';

interface ClientAtendimentosProps {
  clientId: number;
}

const getStatusColor = (status: AtendimentoTicket['status']): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'em_atendimento':
      return 'success';
    case 'pendente':
      return 'warning';
    case 'fechado_bot':
      return 'info';
    case 'fechado':
      return 'default';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: AtendimentoTicket['status']): string => {
  const labels: Record<AtendimentoTicket['status'], string> = {
    pendente: 'Aguardando',
    em_atendimento: 'Em atendimento',
    fechado: 'Fechado',
    fechado_bot: 'Fechado (Bot)',
  };
  return labels[status] || status;
};

const obterDadosPais = (numero: string, ddi?: string | null) => {
  if (ddi) return getCountryByNumber(ddi);
  let limpo = numero.replace(/\D/g, '');
  if (limpo.startsWith('55') && (limpo.length === 12 || limpo.length === 13)) {
    return getCountryByNumber('55');
  }
  if (limpo.length === 10 || limpo.length === 11) {
    return getCountryByNumber('55');
  }
  return getCountryByNumber(limpo);
};

const formatarNumeroComDddParenteses = (numero: string, ddi?: string | null): string => {
  if (!numero) return '';
  let limpo = numero.replace(/\D/g, '');
  
  let ddiEncontrado = ddi || '';
  if (!ddiEncontrado && limpo.startsWith('55') && (limpo.length === 12 || limpo.length === 13)) {
    ddiEncontrado = '55';
  }
  
  if (ddiEncontrado && limpo.startsWith(ddiEncontrado)) {
    limpo = limpo.slice(ddiEncontrado.length);
  }
  
  if (limpo.length === 10) {
    return `${ddiEncontrado ? '+' + ddiEncontrado + ' ' : ''}(${limpo.slice(0, 2)}) ${limpo.slice(2, 6)}-${limpo.slice(6)}`;
  } else if (limpo.length === 11) {
    return `${ddiEncontrado ? '+' + ddiEncontrado + ' ' : ''}(${limpo.slice(0, 2)}) ${limpo.slice(2, 7)}-${limpo.slice(7)}`;
  }
  
  return numero;
};

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

const isTicketAberto = (status: AtendimentoTicket['status']) =>
  status !== 'fechado' && status !== 'fechado_bot';

const ClientAtendimentos: React.FC<ClientAtendimentosProps> = ({ clientId }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [tickets, setTickets] = useState<AtendimentoTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ticketVisualizacao, setTicketVisualizacao] = useState<AtendimentoTicket | null>(null);

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await atendimentoService.getTickets({ cliente_id: clientId });
      const sortedTickets = ((data || []) as AtendimentoTicket[]).sort((a, b) => b.id - a.id);
      setTickets(sortedTickets);
    } catch (err: any) {
      console.error('Erro ao carregar atendimentos:', err);
      setError('Erro ao carregar atendimentos do cliente');
      toast.error('Erro ao carregar atendimentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      loadTickets();
    }
  }, [clientId]);

  const handleAbrirTicket = (ticket: AtendimentoTicket) => {
    if (isTicketAberto(ticket.status)) {
      navigate('/atendimento/atendimentos', { state: { ticketId: ticket.id } });
    } else {
      setTicketVisualizacao(ticket);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="h6" fontWeight="bold">
          Atendimentos do Cliente
        </Typography>
        <Tooltip title="Atualizar">
          <IconButton onClick={loadTickets} disabled={loading} size="small">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Carregando atendimentos...
          </Typography>
        </Box>
      ) : tickets.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Chat sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography color="text.secondary" variant="h6">
            Nenhum atendimento encontrado
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Os tickets de WhatsApp vinculados a este cliente aparecerão aqui
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          elevation={2}
          sx={{
            borderRadius: 2,
            overflowX: 'auto',
            mb: 1,
            border: 'none',
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: 900,
              '& .MuiTableBody-root .MuiTableCell-root': {
                py: 0.6,
                fontSize: '0.78rem',
              },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: (t) => `color-mix(in srgb, ${theme.palette.primary.main} 12%, ${t.palette.background.paper})`,
                    fontWeight: 700,
                    fontSize: '0.73rem',
                    py: 0.8,
                    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    whiteSpace: 'nowrap',
                    color: 'text.primary',
                  },
                }}
              >
                <TableCell sx={{ width: 80 }}>ID</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell>Conexão</TableCell>
                <TableCell sx={{ width: 120 }}>Status</TableCell>
                <TableCell>Atendente</TableCell>
                <TableCell>Setor</TableCell>
                <TableCell>Última mensagem</TableCell>
                <TableCell>Abertura</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((ticket) => {
                const statusColor = getStatusColor(ticket.status);
                const contatoNome = ticket.contato?.nome || 'Contato';
                const contatoNumero = ticket.contato?.numero
                  ? formatarNumeroComDddParenteses(ticket.contato.numero, ticket.contato?.ddi)
                  : '-';

                return (
                  <TableRow
                    key={ticket.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                    }}
                    onClick={() => handleAbrirTicket(ticket)}
                  >
                    <TableCell sx={{ py: 0.6, px: 2 }}>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ fontSize: '0.78rem', color: 'primary.main', fontFamily: 'monospace' }}
                      >
                        #{ticket.id.toString().padStart(5, '0')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.6, px: 2 }}>
                      <Box display="flex" alignItems="center" gap={0.8}>
                        <Avatar
                          src={atendimentoService.getAvatarUrl(ticket.contato?.foto_perfil)}
                          sx={{ width: 24, height: 24, fontSize: '0.65rem' }}
                        >
                          {contatoNome.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', lineHeight: 1.2 }}>
                            {contatoNome}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={0.3}>
                            {ticket.contato?.numero && (() => {
                              const country = obterDadosPais(ticket.contato.numero, ticket.contato.ddi);
                              return country ? (
                                <Tooltip title={country.label}>
                                  <img
                                    loading="lazy"
                                    width="16"
                                    src={new URL(`../../assets/countries/w20/${country.code.toLowerCase()}.png`, import.meta.url).href}
                                    alt={country.label}
                                    style={{ borderRadius: 2 }}
                                  />
                                </Tooltip>
                              ) : null;
                            })()}
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                              {contatoNumero}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.6, px: 2 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500 }}>
                        {ticket.conexao?.nome || '-'}
                      </Typography>
                      {ticket.conexao?.numero && (
                        <Box display="flex" alignItems="center" gap={0.3} mt={0.2}>
                          {(() => {
                            const country = obterDadosPais(ticket.conexao.numero, (ticket.conexao as any).ddi);
                            return country ? (
                              <Tooltip title={country.label}>
                                <img
                                  loading="lazy"
                                  width="16"
                                  src={new URL(`../../assets/countries/w20/${country.code.toLowerCase()}.png`, import.meta.url).href}
                                  alt={country.label}
                                  style={{ borderRadius: 2 }}
                                />
                              </Tooltip>
                            ) : null;
                          })()}
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            {formatarNumeroComDddParenteses(ticket.conexao.numero, (ticket.conexao as any).ddi)}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.6, px: 2 }}>
                      <Chip
                        label={getStatusLabel(ticket.status)}
                        color={statusColor as any}
                        size="small"
                        sx={{ fontSize: '0.66rem', height: 20, fontWeight: 600 }}
                      />
                      {ticket.msgs_nao_lidas > 0 && isTicketAberto(ticket.status) && (
                        <Chip
                          label={ticket.msgs_nao_lidas}
                          color="error"
                          size="small"
                          sx={{ ml: 0.5, fontSize: '0.62rem', height: 18, minWidth: 18 }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.6, px: 2 }}>
                      {ticket.usuario ? (
                        <Box display="flex" alignItems="center" gap={0.8}>
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              bgcolor: alpha(theme.palette.secondary.main, 0.1),
                              color: 'secondary.main',
                            }}
                          >
                            {ticket.usuario.nome.charAt(0)}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                            {ticket.usuario.nome.split(' ')[0]}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                          {ticket.is_bot ? 'Bot' : 'Não atribuído'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.6, px: 2 }}>
                      {ticket.setor ? (
                        <Chip
                          label={ticket.setor.nome}
                          size="small"
                          sx={{
                            fontSize: '0.66rem',
                            height: 20,
                            fontWeight: 600,
                            bgcolor: ticket.setor.cor ? alpha(ticket.setor.cor, 0.15) : undefined,
                            color: ticket.setor.cor || undefined,
                            borderColor: ticket.setor.cor || undefined,
                          }}
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">-</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.6, px: 2, maxWidth: 200 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.75rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {ticket.ultima_mensagem || 'Sem mensagens'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.6, px: 2 }}>
                      <Tooltip title={ticket.data_fechamento ? `Fechado: ${formatDate(ticket.data_fechamento)}` : ''}>
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500 }}>
                            {formatDate(ticket.createdAt).split(',')[0]}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            {formatDate(ticket.createdAt).split(',')[1]?.trim() || ''}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.6, px: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title={isTicketAberto(ticket.status) ? 'Abrir no chat' : 'Visualizar conversa'}>
                          <IconButton
                            size="small"
                            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAbrirTicket(ticket);
                            }}
                          >
                            {isTicketAberto(ticket.status) ? (
                              <OpenInNew sx={{ fontSize: 18 }} color="primary" />
                            ) : (
                              <Visibility sx={{ fontSize: 18 }} color="primary" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 0.8,
              px: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
              Mostrando <strong>{tickets.length}</strong> atendimento{tickets.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </TableContainer>
      )}

      <VisualizarTicketDialog
        open={Boolean(ticketVisualizacao)}
        ticket={ticketVisualizacao}
        onClose={() => setTicketVisualizacao(null)}
        onTicketReopened={(ticket) => {
          setTickets((prev) => prev.map((item) => (item.id === ticket.id ? ticket : item)));
          setTicketVisualizacao(null);
          navigate('/atendimento/atendimentos', { state: { ticketId: ticket.id } });
        }}
      />
    </Box>
  );
};

export default ClientAtendimentos;
